import { useMemo, type ReactNode } from "react";
import { AlertCircle, Building2, Layers3, Loader2, Mail, Phone, User } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { usePageTitle } from "@/hooks/usePageTitle";

type ScopeCompany = {
  id: number;
  nomeFantasia?: string | null;
  razaoSocial?: string | null;
  name?: string | null;
  cnpj?: string | null;
  responsibleName?: string | null;
  email?: string | null;
  phone?: string | null;
  sector?: string | null;
  isMatriz?: boolean | null;
  isControladora?: boolean | null;
  isLinkedCompany?: boolean;
};

function getCompanyName(company: ScopeCompany) {
  return company.nomeFantasia || company.razaoSocial || company.name || "Empresa cadastrada";
}

function getHierarchyLabel(company: ScopeCompany) {
  if (company.isMatriz && company.isControladora) return "Matriz e controladora";
  if (company.isMatriz) return "Matriz";
  if (company.isControladora) return "Controladora";
  return "Empresa do escopo";
}

export default function MinhaEmpresa() {
  usePageTitle("Minha Empresa");
  const { user } = useAuth();
  const companyId = (user as any)?.companyId;

  const { data, isLoading, error } = trpc.clients.listMyScopeCompanies.useQuery(undefined, {
    enabled: !!companyId,
  });

  const companies = useMemo(() => {
    const items: ScopeCompany[] = data?.items ?? [];
    return [...items].sort((a, b) => {
      if (Boolean(a.isLinkedCompany) !== Boolean(b.isLinkedCompany)) {
        return a.isLinkedCompany ? -1 : 1;
      }
      return getCompanyName(a).localeCompare(getCompanyName(b), "pt-BR");
    });
  }, [data]);

  const hasScopeCompanies = companies.length > 1;

  return (
    <SalviaLayout>
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <section className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-sm lg:p-8">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-[#C0FF00]">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight">Minha Empresa</h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-300">
                {hasScopeCompanies
                  ? "Como sua empresa está em uma estrutura hierárquica, esta área reúne os dados principais da empresa vinculada e das demais empresas disponíveis no seu escopo de consulta."
                  : "Aqui ficam os dados principais da empresa para consulta rápida no dia a dia."}
              </p>
            </div>
          </div>
        </section>

        {!companyId && (
          <section className="rounded-3xl border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-900">Empresa não vinculada</p>
                <p className="mt-1 text-sm leading-6 text-yellow-800">
                  Seu usuário ainda não está ligado a uma empresa. Se precisar, fale com o canal responsável.
                </p>
              </div>
            </div>
          </section>
        )}

        {isLoading && (
          <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-center gap-3 py-10 text-slate-600">
              <Loader2 className="h-6 w-6 animate-spin text-[#6B47DC]" />
              <span>Carregando dados das empresas...</span>
            </div>
          </section>
        )}

        {error && (
          <section className="rounded-3xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">Não foi possível carregar os dados das empresas</p>
                <p className="mt-1 text-sm leading-6 text-red-800">{error.message}</p>
              </div>
            </div>
          </section>
        )}

        {!isLoading && !error && !!companyId && companies.length > 0 && (
          <section className="rounded-3xl border border-indigo-100 bg-indigo-50/70 p-5 text-sm text-slate-700 shadow-sm">
            <div className="flex items-start gap-3">
              <Layers3 className="mt-0.5 h-5 w-5 text-indigo-600" />
              <div>
                <p className="font-medium text-slate-900">
                  {hasScopeCompanies
                    ? `${companies.length} empresas disponíveis no seu escopo hierárquico`
                    : "1 empresa disponível no seu acesso"}
                </p>
                <p className="mt-1 leading-6 text-slate-600">
                  {hasScopeCompanies
                    ? "A tela continua em modo de consulta rápida, mas agora também apresenta as empresas pertencentes ao escopo da sua matriz, controladora ou matriz e controladora."
                    : "A tela continua em modo de consulta rápida e apresenta a empresa vinculada ao seu acesso."}
                </p>
              </div>
            </div>
          </section>
        )}

        {!isLoading && !error && !!companyId && companies.length === 0 && (
          <section className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <p className="font-medium text-slate-900">Nenhuma empresa encontrada</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Não foi possível localizar empresas para o seu escopo atual de consulta.
            </p>
          </section>
        )}

        {companies.map((company) => {
          const hierarchyLabel = getHierarchyLabel(company);
          const companyName = getCompanyName(company);

          return (
            <section key={company.id} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {company.isLinkedCompany ? "Empresa vinculada ao usuário" : "Empresa do escopo"}
                    </span>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                      {hierarchyLabel}
                    </span>
                  </div>
                  <p className="mt-4 text-sm font-medium text-slate-500">Empresa</p>
                  <h2 className="mt-1 text-2xl font-semibold text-slate-900">{companyName}</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Consulte os dados principais desta empresa sem precisar abrir um cadastro completo.
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">CNPJ</p>
                  <p className="mt-1">{company.cnpj || "—"}</p>
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <InfoCard
                  icon={<User className="h-4 w-4" />}
                  label="Responsável"
                  value={company.responsibleName || "Não informado"}
                />
                <InfoCard
                  icon={<Mail className="h-4 w-4" />}
                  label="Email"
                  value={company.email || "Não informado"}
                />
                <InfoCard
                  icon={<Phone className="h-4 w-4" />}
                  label="Telefone"
                  value={company.phone || "Não informado"}
                />
                <InfoCard
                  icon={<Building2 className="h-4 w-4" />}
                  label="Setor"
                  value={company.sector || "Não informado"}
                />
              </div>
            </section>
          );
        })}

        {!!companyId && companies.length > 0 && (
          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-600 shadow-sm">
            Se precisar atualizar alguma informação, fale com o canal responsável. Nesta etapa, a tela segue focada apenas em consulta rápida, sem alterar permissões operacionais.
          </section>
        )}
      </div>
    </SalviaLayout>
  );
}

function InfoCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
        <span className="text-slate-700">{icon}</span>
        {label}
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}
